import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import Mascot from "@/components/Mascot";
import fachada from "@/assets/fachada-alpha.jpg";
import MascotGuide from "@/components/MascotGuide";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, Zap, Truck, Store, Wrench, Smartphone, Headphones, MessageCircle,
  Users, ArrowRight, BatteryCharging, ScanLine, Cpu, Wallet, Cable, Plug, Watch,
  Star, ChevronLeft, ChevronRight, HeartPulse, Instagram, Facebook, Globe,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const ICONS = {
  shield: ShieldCheck, cpu: Cpu, zap: Zap, store: Store, scan: ScanLine,
  battery: BatteryCharging, phone: Smartphone, cable: Cable, plug: Plug,
  watch: Watch, headphones: Headphones, truck: Truck,
};
const Icon = ({ name, ...p }) => { const C = ICONS[name] || Wrench; return <C {...p} />; };

export default function PublicHome() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const carouselRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/public/home`).then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  if (data === null)
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0C]">
      <div className="h-10 w-10 rounded-full border-2 border-gold border-t-transparent animate-spin" /></div>;
  if (data === false)
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0C] text-zinc-400">Não foi possível carregar a página.</div>;

  const wa = data.company?.whatsapp || "";
  const tpl = data.whatsapp_templates || {};
  const links = data.public_links || {};
  const social = data.social_links || {};
  const sp = data.social_proof || {};
  const openWa = (message) => window.open(`https://wa.me/${wa}?text=${encodeURIComponent(message || tpl.general || "Olá!")}`, "_blank");
  const openLink = (url, fallbackId) => (url ? window.open(url, "_blank") : scrollTo(fallbackId));

  // Cards inteligentes — assistência técnica em primeiro lugar
  const smartCards = [
    { key: "acessorios", icon: Headphones, title: "Comprar acessórios", desc: "Veja capinhas, carregadores, cabos, películas e outros acessórios disponíveis.", cta: "Ver acessórios", onClick: () => openLink(links.loja_virtual, "acessorios") },
    { key: "express", icon: Zap, title: "Alpha Express", desc: "Buscamos e devolvemos seu aparelho no mesmo dia, quando disponível.", cta: "Solicitar", onClick: () => (links.alpha_express ? window.open(links.alpha_express, "_blank") : openWa(tpl.express)) },
    { key: "mercadolivre", icon: Store, title: "Agência Mercado Livre", desc: "Agência oficial: postagens, retiradas e devoluções com atendimento rápido e seguro.", cta: "Falar agora", onClick: () => openWa(tpl.mercadolivre) },
    { key: "whatsapp", icon: MessageCircle, title: "Falar no WhatsApp", desc: "Atendimento rápido para dúvidas, orçamento e suporte.", cta: "Conversar", onClick: () => openWa(tpl.general) },
  ];

  const slide = (dir) => carouselRef.current?.scrollBy({ left: dir * 320, behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white" data-testid="public-home">
      <header className="alpha-glass sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 h-16 flex items-center">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl flex items-center justify-center font-display font-extrabold text-black"
              style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>A</div>
            <div className="leading-none">
              <div className="font-display font-extrabold tracking-tight">Alpha<span className="alpha-gold-text">Prime</span></div>
              <div className="text-[10px] text-zinc-500 tracking-widest">ASSISTÊNCIA PREMIUM</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-6 ml-10 text-sm text-zinc-400">
            <button onClick={() => scrollTo("servicos")} className="hover:text-white">Serviços</button>
            <button onClick={() => scrollTo("celulares")} className="hover:text-white">Celulares</button>
            <button onClick={() => scrollTo("acessorios")} className="hover:text-white">Acessórios</button>
            <button onClick={() => scrollTo("express")} className="hover:text-white">Alpha Express</button>
            <button onClick={() => scrollTo("sobre")} className="hover:text-white">Sobre</button>
          </nav>
          <div className="flex-1" />
          <Button data-testid="team-area-btn" onClick={() => navigate("/equipe")}
            variant="outline" className="border-gold/40 text-gold-bright hover:bg-gold/10">
            <Users size={16} className="mr-2" /> Área da Equipe
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-24 h-96 w-96 rounded-full bg-gold/10 blur-3xl" />
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center relative">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <span data-testid="hero-badge" className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-gold/10 border border-gold/30 text-gold-bright mb-4">
              <Star size={13} className="fill-gold-bright text-gold-bright" /> {data.hero?.badge}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold tracking-tight leading-[1.05]">
              {data.hero?.title}
            </h1>
            <p className="text-zinc-400 mt-5 text-base sm:text-lg max-w-lg">{data.hero?.subtitle}</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button data-testid="hero-repair-btn" onClick={() => openWa(tpl.repair)}
                className="h-12 px-6 text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>
                <Wrench size={18} className="mr-2" /> {data.hero?.cta_primary}
              </Button>
              <Button data-testid="hero-offers-btn" onClick={() => scrollTo("celulares")} variant="outline" className="h-12 px-6 border-white/15 hover:bg-white/5">
                {data.hero?.cta_secondary} <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8" data-testid="trust-bar">
              {(data.trust_badges || []).map((b) => (
                <div key={b.title} className="flex items-center gap-2 text-sm text-zinc-300">
                  <span className="h-8 w-8 rounded-lg bg-gold/10 flex items-center justify-center shrink-0">
                    <Icon name={b.icon} size={15} className="text-gold" />
                  </span>
                  <span className="leading-tight">{b.title}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
            className="relative" data-testid="hero-facade">
            <div className="absolute -inset-4 rounded-[2rem] bg-gold/15 blur-3xl" />
            <div className="relative rounded-3xl overflow-hidden border border-gold/30 shadow-2xl">
              <img src={fachada} alt="Fachada da Alpha Prime — Riacho Fundo 2, Brasília" className="w-full object-cover aspect-[4/3]" />
              <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-black/60 backdrop-blur border border-white/10 text-white">
                <Store size={13} className="text-gold-bright" /> Riacho Fundo 2 · Brasília/DF
              </span>
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-3xl pointer-events-none" />
            </div>
            <div className="absolute -bottom-5 -left-4 sm:-left-6">
              <div className="absolute inset-0 rounded-full bg-gold/25 blur-xl" />
              <Mascot size={104} className="relative drop-shadow-xl" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cards inteligentes — CTA principal em destaque */}
      <section id="ajuda" className="max-w-6xl mx-auto px-4 lg:px-8 mb-10">
        <button data-testid="smart-card-repair" onClick={() => openWa(tpl.repair)}
          className="w-full text-left rounded-2xl p-6 lg:p-8 relative overflow-hidden group transition-all duration-200 hover:translate-y-[-2px]"
          style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.14), rgba(10,10,12,0.4))", border: "1px solid rgba(212,175,55,0.35)" }}>
          <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gold/15 blur-3xl" />
          <div className="relative flex items-center gap-5 flex-wrap">
            <span className="h-14 w-14 rounded-2xl flex items-center justify-center text-black shrink-0"
              style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}><Wrench size={26} /></span>
            <div className="flex-1 min-w-[220px]">
              <div className="text-xs text-gold-bright font-semibold tracking-wide mb-1">ASSISTÊNCIA TÉCNICA · DESTAQUE</div>
              <h3 className="text-xl lg:text-2xl font-display font-extrabold text-white">Consertar meu celular</h3>
              <p className="text-zinc-300 mt-1 max-w-xl">Receba um orçamento rápido, transparente e sem compromisso para o seu aparelho.</p>
            </div>
            <span className="inline-flex items-center gap-2 text-black font-semibold px-5 py-3 rounded-full shrink-0"
              style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>
              <MessageCircle size={17} /> Fazer orçamento
            </span>
          </div>
        </button>

        <button data-testid="smart-card-boleto-highlight" onClick={() => navigate("/boleto")}
          className="w-full text-left rounded-2xl p-6 lg:p-8 relative overflow-hidden group transition-all duration-200 hover:translate-y-[-2px] mt-4"
          style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.14), rgba(10,10,12,0.4))", border: "1px solid rgba(16,185,129,0.35)" }}>
          <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-emerald-500/15 blur-3xl" />
          <div className="relative flex items-center gap-5 flex-wrap">
            <span className="h-14 w-14 rounded-2xl flex items-center justify-center text-black shrink-0"
              style={{ background: "linear-gradient(135deg,#34D399,#10B981 55%,#059669)" }}><Wallet size={26} /></span>
            <div className="flex-1 min-w-[220px]">
              <div className="text-xs font-semibold tracking-wide mb-1 text-emerald-300">CELULAR NO BOLETO · DESTAQUE</div>
              <h3 className="text-xl lg:text-2xl font-display font-extrabold text-white">Compre seu celular no boleto</h3>
              <p className="text-zinc-300 mt-1 max-w-xl">Parcelado no boleto pelas financeiras parceiras — inclusive para negativados. Veja os aparelhos disponíveis.</p>
            </div>
            <span className="inline-flex items-center gap-2 text-black font-semibold px-5 py-3 rounded-full shrink-0"
              style={{ background: "linear-gradient(135deg,#34D399,#10B981 55%,#059669)" }}>
              <Wallet size={17} /> Ver aparelhos
            </span>
          </div>
        </button>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
          {smartCards.map((c) => (
            <button key={c.key} data-testid={`smart-card-${c.key}`} onClick={c.onClick}
              className="alpha-card p-5 text-left flex flex-col gap-3 hover:translate-y-[-2px]">
              <span className="h-11 w-11 rounded-xl bg-gold/10 flex items-center justify-center"><c.icon size={20} className="text-gold" /></span>
              <div className="flex-1">
                <h3 className="font-semibold text-white">{c.title}</h3>
                <p className="text-sm text-zinc-500 mt-1 leading-snug">{c.desc}</p>
              </div>
              <span className="inline-flex items-center gap-1 text-sm text-gold-bright font-medium">{c.cta} <ArrowRight size={14} /></span>
            </button>
          ))}
        </div>
      </section>

      {/* Serviços */}
      <Section id="servicos" title="Serviços" subtitle="Especialistas em cada detalhe do seu aparelho.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {(data.services || []).map((s) => (
            <div key={s.title} className="alpha-card p-6">
              <span className="h-11 w-11 rounded-xl bg-gold/10 flex items-center justify-center mb-4"><Icon name={s.icon} size={20} className="text-gold" /></span>
              <h3 className="font-semibold text-white">{s.title}</h3>
              <p className="text-sm text-zinc-500 mt-1">{s.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Conteúdo SEO — reparos, marcas e regiões atendidas */}
      <Section id="reparos" title="Reparos que fazemos" subtitle="Especialistas em iPhone, Samsung, Xiaomi, Redmi, Poco e Motorola.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="alpha-card p-6">
            <h3 className="font-semibold text-white">Troca de tela</h3>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">Trocamos a tela de iPhone (11, 12, 13, 14, 15 e 16), Samsung (A56, A55, A36 e linha S/Galaxy), Xiaomi, Redmi e Poco, com peças de qualidade e garantia.</p>
          </div>
          <div className="alpha-card p-6">
            <h3 className="font-semibold text-white">Bateria e conector de carga</h3>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">Troca de bateria de iPhone, Samsung, Xiaomi e Motorola. Seu celular não carrega ou carrega mal? Consertamos a entrada/conector de carregador.</p>
          </div>
          <div className="alpha-card p-6">
            <h3 className="font-semibold text-white">Placa e microssoldagem</h3>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">Reparo avançado de placa e microssoldagem. Celular não liga, caiu na água ou com defeito na placa? Diagnóstico com microscópio e técnicos especializados.</p>
          </div>
          <div className="alpha-card p-6">
            <h3 className="font-semibold text-white">Recuperação e mais</h3>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">Recuperação de aparelho molhado e de dados, conserto de Face ID, Touch ID, câmera e troca de vidro traseiro (back glass) de iPhone.</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          <div className="alpha-card p-6">
            <h3 className="font-semibold text-white">Onde atendemos</h3>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">Assistência técnica de celular no <strong className="text-zinc-200">Riacho Fundo 2</strong>, Riacho Fundo, Recanto das Emas, Núcleo Bandeirante e toda a Brasília Sul. Orçamento sem compromisso, garantia de 6 meses e conserto no mesmo dia na maioria dos reparos.</p>
          </div>
          <div className="alpha-card p-6">
            <h3 className="font-semibold text-white">Alpha Express e conveniência</h3>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">Busca e entrega do seu celular. Somos ponto <strong className="text-zinc-200">Mercado Livre</strong> e <strong className="text-zinc-200">Correios</strong> no Riacho Fundo 2: poste, devolva e retire encomendas. Também temos acessórios: capinhas, películas, carregadores USB-C e fones Bluetooth.</p>
          </div>
        </div>
      </Section>

      {/* Celulares */}
      <Section id="celulares" title="Celulares à venda" subtitle="Novos e seminovos, com garantia.">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="phones-grid">
          {(data.phones || []).map((p) => (
            <div key={p.id} data-testid={`phone-card-${p.id}`} className="alpha-card overflow-hidden group">
              <div className="aspect-square overflow-hidden bg-[#0A0A0C]">
                <img src={p.image} alt={p.model} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              </div>
              <div className="p-5">
                <h3 className="font-semibold text-white leading-tight">{p.model}</h3>
                <div className="flex flex-wrap gap-1.5 mt-2 text-xs">
                  <Spec>{p.storage}</Spec>
                  <Spec>{p.color}</Spec>
                  {p.battery_health ? <Spec className="inline-flex items-center gap-1"><HeartPulse size={11} className="text-green-400" />Saúde {p.battery_health}%</Spec> : null}
                </div>
                <div className="inline-flex items-center gap-1 mt-2 text-xs text-gold-bright">
                  <ShieldCheck size={12} /> {data.warranty_label}
                </div>
                <div className="mt-4 flex items-center justify-between gap-2">
                  <span className="text-lg font-mono-kpi font-extrabold text-gold-bright">{money(p.price)}</span>
                  <Button data-testid={`phone-buy-${p.id}`} size="sm"
                    onClick={() => openWa((tpl.phone_interest || "Tenho interesse no {produto}").replace("{produto}", p.model))}
                    className="bg-green-600 hover:bg-green-500 text-white"><MessageCircle size={14} className="mr-1" />Comprar</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Acessórios — carrossel */}
      <section id="acessorios" className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-6 gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-display font-extrabold tracking-tight">Acessórios</h2>
            <p className="text-zinc-400 mt-1">Originais e selecionados para durar.</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="acc-prev" onClick={() => slide(-1)} className="h-10 w-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/5"><ChevronLeft size={18} /></button>
            <button data-testid="acc-next" onClick={() => slide(1)} className="h-10 w-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/5"><ChevronRight size={18} /></button>
          </div>
        </div>
        <div ref={carouselRef} data-testid="accessories-carousel"
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2" style={{ scrollbarWidth: "none" }}>
          {(data.accessories || []).map((a) => (
            <button key={a.id} data-testid={`acc-${a.category}`} onClick={() => openLink(links.loja_virtual, "acessorios")}
              className="snap-start shrink-0 w-64 alpha-card overflow-hidden group text-left">
              <div className="aspect-[4/3] overflow-hidden bg-[#0A0A0C] relative">
                <img src={a.image} alt={a.category} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/90 to-transparent flex items-center gap-2">
                  <Icon name={a.icon} size={16} className="text-gold" />
                  <span className="font-semibold text-white">{a.category}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Alpha Express */}
      <section id="express" className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
        <div className="relative alpha-card overflow-hidden p-8 lg:p-12 flex flex-col lg:flex-row items-center gap-8">
          <div className="absolute -left-20 -bottom-20 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />
          <div className="flex-1 relative">
            <span className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full bg-gold/10 border border-gold/30 text-gold-bright mb-3">
              <Zap size={13} /> Alpha Express
            </span>
            <h2 className="text-3xl lg:text-4xl font-display font-extrabold tracking-tight">{data.express?.title}</h2>
            <p className="text-zinc-400 mt-3 max-w-lg">{data.express?.text}</p>
            <Button data-testid="express-btn" onClick={() => (links.alpha_express ? window.open(links.alpha_express, "_blank") : openWa(tpl.express))}
              className="mt-6 h-12 px-6 text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>
              Solicitar Alpha Express
            </Button>
          </div>
          <Mascot size={180} className="relative hidden lg:block" />
        </div>
      </section>

      {/* Prova social */}
      <Section id="prova-social" title="Quem já passou por aqui" subtitle="Confiança construída aparelho por aparelho.">
        <div className="grid sm:grid-cols-3 gap-5" data-testid="social-proof">
          <div className="alpha-card p-8 text-center">
            <div className="flex justify-center gap-1 mb-2">
              {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={20} className="fill-gold-bright text-gold-bright" />)}
            </div>
            <div className="text-3xl font-mono-kpi font-extrabold text-white">{sp.rating}</div>
            <div className="text-sm text-zinc-500 mt-1">Avaliação {sp.rating_source}</div>
          </div>
          <div className="alpha-card p-8 text-center">
            <div className="text-3xl font-mono-kpi font-extrabold alpha-gold-text">{sp.devices_served}</div>
            <div className="text-sm text-zinc-500 mt-1">{sp.devices_label}</div>
          </div>
          <div className="alpha-card p-8 text-center">
            <div className="text-3xl font-mono-kpi font-extrabold text-white">{sp.ml_count}</div>
            <div className="text-sm text-zinc-500 mt-1">{sp.ml_label}</div>
          </div>
        </div>
      </Section>

      {/* Mercado Livre institucional (atendimento pelo WhatsApp) */}
      <Section id="mercadolivre" title="Agência Mercado Livre" subtitle="Ponto oficial para postagens, coletas e retiradas.">
        <div className="alpha-card p-8 flex items-center gap-6 flex-wrap">
          <span className="h-16 w-16 rounded-2xl bg-yellow-400/15 flex items-center justify-center shrink-0"><Store size={28} className="text-yellow-400" /></span>
          <div className="flex-1 min-w-[220px]"><p className="text-zinc-300">{data.mercadolivre_text}</p></div>
          <Button data-testid="ml-btn" onClick={() => openWa(tpl.mercadolivre)} variant="outline" className="border-gold/40 text-gold-bright hover:bg-gold/10">Falar no WhatsApp</Button>
        </div>
      </Section>

      {/* Sobre */}
      <Section id="sobre" title="Sobre a Alpha Prime" subtitle="Tecnologia, confiança e atendimento de verdade.">
        <div className="alpha-card p-8"><p className="text-zinc-300 leading-relaxed max-w-3xl">{data.about_text}</p></div>
      </Section>

      {/* Footer dinâmico */}
      <footer className="border-t border-white/10 mt-8" data-testid="public-footer">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10 grid sm:grid-cols-3 gap-8">
          <div>
            <div className="font-display font-extrabold text-lg">Alpha<span className="alpha-gold-text">Prime</span></div>
            <p className="text-sm text-zinc-500 mt-2">Assistência técnica premium, seminovos e acessórios.</p>
            <div className="flex items-center gap-3 mt-4">
              {social.instagram ? <a href={social.instagram} target="_blank" rel="noreferrer" data-testid="social-instagram" className="h-9 w-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-gold/15"><Instagram size={16} className="text-gold-bright" /></a> : null}
              {social.facebook ? <a href={social.facebook} target="_blank" rel="noreferrer" data-testid="social-facebook" className="h-9 w-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-gold/15"><Facebook size={16} className="text-gold-bright" /></a> : null}
            </div>
          </div>
          <div className="text-sm text-zinc-400">
            <div className="text-white font-semibold mb-2">Contato</div>
            <p>WhatsApp / Tel: {data.company?.phone}</p>
            <p>{data.company?.address}</p>
            <p>{data.company?.hours}</p>
            {data.website ? <p className="inline-flex items-center gap-1 mt-1"><Globe size={13} className="text-gold" /> {data.website}</p> : null}
          </div>
          <div>
            <Button data-testid="footer-whatsapp-btn" onClick={() => openWa(tpl.general)}
              className="bg-green-600 hover:bg-green-500 text-white w-full sm:w-auto">
              <MessageCircle size={16} className="mr-2" /> Falar no WhatsApp
            </Button>
            <button onClick={() => navigate("/equipe")} className="block mt-4 text-xs text-zinc-600 hover:text-gold-bright">Área da Equipe →</button>
          </div>
        </div>
        <div className="text-center text-xs text-zinc-600 pb-6">© {new Date().getFullYear()} {data.company?.name} · Todos os direitos reservados</div>
      </footer>

      <MascotGuide phrases={data.mascot_phrases || []} onTalk={() => openWa(tpl.general)} />
    </div>
  );
}

function Section({ id, title, subtitle, children }) {
  return (
    <section id={id} className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-display font-extrabold tracking-tight">{title}</h2>
        {subtitle && <p className="text-zinc-400 mt-1">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

const Spec = ({ children, className = "" }) => (
  <span className={`px-2 py-0.5 rounded-full bg-white/5 text-zinc-300 ${className}`}>{children}</span>
);
